import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExploreServiceState, updateDistanceAway, updateFilters } from '../redux/exploreServiceSlice';
import { getValidDistanceAway } from './filterConfigs/useDistanceAwayFilterConfig';
import { getValidElevationGain } from './filterConfigs/useElevationGainFilterConfig';
import { getValidHighestPoint } from './filterConfigs/useHighestPointFilterConfig';
import { getValidLength } from './filterConfigs/useLengthFilterConfig';

/**
 * Listens to changes in the displayMetric value and updates unit-dependent filter values in state so that
 * filters don't break when a user signs into an account that uses different units
 * @param displayMetric whether to use metric units - passed in for ease in testing
 */
const useUnitAwareExploreFilters = (displayMetric: boolean) => {
  const dispatch = useDispatch();
  const { distanceAway, filters } = useSelector((state: { exploreService: ExploreServiceState }) => state.exploreService);

  useEffect(() => {
    if (distanceAway) {
      const { center, distance } = distanceAway;
      const validDistance = getValidDistanceAway(distance, displayMetric);

      if (validDistance && validDistance !== distance) {
        dispatch(updateDistanceAway({ center, distance: validDistance }));
      }
    }

    // We only want to run this when displayMetric is changed, thats the whole point of the hook
  }, [displayMetric]);

  useEffect(() => {
    let filtersWereUpdated = false;
    const newFilters = JSON.parse(JSON.stringify(filters));

    if (filters.elevationGain) {
      const { min, max } = filters.elevationGain;
      const validMin = min === undefined ? undefined : getValidElevationGain(min, displayMetric);
      const validMax = max === undefined ? undefined : getValidElevationGain(max, displayMetric);

      if (min !== validMin || max !== validMax) {
        filtersWereUpdated = true;
        if (validMin && !validMax) {
          newFilters.elevationGain = { min: validMin };
        } else if (!validMin && validMax) {
          newFilters.elevationGain = { max: validMax };
        } else if (validMin && validMax) {
          newFilters.elevationGain = { min: validMin, max: validMax };
        }
      }
    }

    if (filters.highestPoint) {
      const { min, max } = filters.highestPoint;
      const validMin = min === undefined ? undefined : getValidHighestPoint(min, displayMetric);
      const validMax = max === undefined ? undefined : getValidHighestPoint(max, displayMetric);

      if (min !== validMin || max !== validMax) {
        filtersWereUpdated = true;
        if (validMin && !validMax) {
          newFilters.highestPoint = { min: validMin };
        } else if (!validMin && validMax) {
          newFilters.highestPoint = { max: validMax };
        } else if (validMin && validMax) {
          newFilters.highestPoint = { min: validMin, max: validMax };
        }
      }
    }

    if (filters.length) {
      const { min, max } = filters.length;
      const validMin = min === undefined ? undefined : getValidLength(min, displayMetric);
      const validMax = max === undefined ? undefined : getValidLength(max, displayMetric);

      if (min !== validMin || max !== validMax) {
        filtersWereUpdated = true;
        if (validMin && !validMax) {
          newFilters.length = { min: validMin };
        } else if (!validMin && validMax) {
          newFilters.length = { max: validMax };
        } else if (validMin && validMax) {
          newFilters.length = { min: validMin, max: validMax };
        }
      }
    }

    if (filtersWereUpdated) {
      dispatch(updateFilters(newFilters));
    }
    // We only want to run this when displayMetric is changed, thats the whole point of the hook
  }, [displayMetric]);
};

export default useUnitAwareExploreFilters;
